import {
  getGetApiLineItemApiLineItemPropertiesQueryKey,
  usePutApiLineItemApiLineItemProperties,
} from "../../openapi/backendApi";
import { TextFieldSimple } from "../MuiWrappers/TextFieldSimple";

import { JSX, KeyboardEvent } from "react";

import { SxProps, Theme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

interface ILineItemPropertyField {
  lineItemId: string;
  field: "notes" | "customerId";
  value: string;
  savedValue: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  sx?: SxProps<Theme>;
}

/**
 * Text field for a single line item property. Saves on blur or Enter, only
 * when the value differs from the saved one. Each instance has its own
 * mutation, so saving one field doesn't disable the others.
 */
export function LineItemPropertyField({
  lineItemId,
  field,
  value,
  savedValue,
  onChange,
  ariaLabel,
  sx,
}: ILineItemPropertyField): JSX.Element {
  const queryClient = useQueryClient();
  const { mutate, isPending } = usePutApiLineItemApiLineItemProperties({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getGetApiLineItemApiLineItemPropertiesQueryKey({
            lineItemId,
          }),
        }),
    },
  });

  const handleBlur = (newValue: string): void => {
    if (newValue === savedValue) {
      return;
    }
    mutate({ params: { lineItemId }, data: { [field]: newValue } });
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter") {
      return;
    }
    event.preventDefault();
    // target is the <input>; blurring it triggers handleBlur
    (event.target as HTMLElement).blur();
  };

  return (
    <TextFieldSimple
      value={value}
      disabled={isPending}
      ariaLabel={ariaLabel}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onChange={(e) => onChange(e.target.value)}
      sx={sx}
    />
  );
}
